import { useState } from 'react'
import styled from 'styled-components'
import {
  ButtonGroup as AlliumButtonGroup,
  StackView,
  useResponsiveProp,
  Spacer,
  FlexGrid,
} from '@telus-uds/components-web'

import { findItemIndex } from '../../../utils/findItem'
import useKoodoAnalyticsEvent from '../../../../hooks/useKoodoAnalyticsEvent'
import { mapDataAttrsToDataSet } from '../../../utils/dataAttributes'
import alloyEvents from '../../../utils/alloyAnalytics/tabs'
import { ContentfulAnalyticsEventType } from 'src/siteBuilder/utils/types/analytics'

type Alignment = 'left' | 'center' | 'right'

export interface IButtonGroupTabsProps {
  id: string
  label: string
  content: JSX.Element
}

export interface ButtonGroupTabsProps {
  sysID: string
  entryTitle: string
  buttonGroupTabs: IButtonGroupTabsProps[]
  defaultTabItemId?: string
  linkAnalyticsEventsArray?: ContentfulAnalyticsEventType[]
  styleVariant?: string
  desktopAlignment?: Alignment
  mobileAlignment?: Alignment
  buttonWidth?: string
  buttonSize?: string
}

const TabsContainer = styled.div`
  display: grid;
`

const TabPanel = styled.div.attrs<{ isActive: boolean }>(({ isActive }) => ({
  'aria-hidden': !isActive,
  ...((!isActive && { inert: '' }) as object),
}))<{ isActive: boolean }>`
  grid-row: 1;
  grid-column: 1;
  ${({ isActive }) =>
    !isActive &&
    `
    height: 0;
    overflow: hidden;
  `}
`

const ButtonGroupTabs = ({
  sysID,
  entryTitle,
  buttonGroupTabs,
  linkAnalyticsEventsArray = undefined,
  defaultTabItemId,
  styleVariant = '',
  desktopAlignment = 'center',
  mobileAlignment = 'center',
  buttonWidth = 'responsive',
  buttonSize = 'small',
  ...rest
}: ButtonGroupTabsProps) => {
  const [isAnalyticsTriggered, setIsAnalyticsTriggered] = useState(false)
  const [values, setValues] = useState([defaultTabItemId])
  const [selectedTabIndex, setSelectedTabIndex] = useState(findItemIndex(buttonGroupTabs, defaultTabItemId))
  const selectedAnalyticsEvent: ContentfulAnalyticsEventType = linkAnalyticsEventsArray
    ? linkAnalyticsEventsArray[selectedTabIndex]
    : null

  const justifyContent = useResponsiveProp({
    xs: mobileAlignment,
    sm: mobileAlignment,
    md: desktopAlignment,
    lg: desktopAlignment,
  })

  const setButtonWidth = buttonWidth === 'default' ? undefined : buttonWidth

  useKoodoAnalyticsEvent(selectedAnalyticsEvent, isAnalyticsTriggered)

  if (!buttonGroupTabs) return null

  return (
    <div data-testid={'button-group-testid'}>
      <StackView>
        <FlexGrid>
          <FlexGrid.Row>
            <FlexGrid.Col>
              <div
                style={{
                  alignItems: 'center',
                  justifyContent,
                  flexGrow: 1,
                  display: 'flex',
                }}
              >
                <AlliumButtonGroup
                  values={values}
                  onChange={(selected: string[]) => {
                    setValues(selected)
                    setSelectedTabIndex(findItemIndex(buttonGroupTabs, selected[0]))
                    setIsAnalyticsTriggered(true)
                    alloyEvents.tabChange({ sysID, entryTitle, tabID: selected[0] })
                  }}
                  items={buttonGroupTabs}
                  dataSet={mapDataAttrsToDataSet(rest)}
                  variant={{ style: styleVariant, size: buttonSize, width: setButtonWidth }}
                />
              </div>
            </FlexGrid.Col>
          </FlexGrid.Row>
        </FlexGrid>
        <Spacer space={{ xs: 4, md: 5 }} />
        <TabsContainer>
          {buttonGroupTabs.map((tab, index) => {
            const isActive = selectedTabIndex === index
            return (
              <TabPanel key={tab.id} isActive={isActive}>
                {tab.content}
              </TabPanel>
            )
          })}
        </TabsContainer>
      </StackView>
    </div>
  )
}

export default ButtonGroupTabs
